// ============================================================
// MANIC MEETER — English content: upgrades, perks, vendors, endings
// ============================================================

import {
  AchievementId, EndingType, PermanentUnlockId, PowerupId, StatUpgradeId, WeekendEventType,
} from "../../types";
import type {
  EndingContent, MeetingTypeContent, PermanentUnlockContent, PositiveEventContent, TextEntry,
  VendorContent,
} from "../contract";

export const STAT_UPGRADES: Record<StatUpgradeId, TextEntry> = {
  [StatUpgradeId.Gehalt]: { name: "💰 Salary Negotiation", description: "Starting salary +€200 per level" },
  [StatUpgradeId.MaxMuedigkeit]: { name: "☕ Caffeine Tolerance", description: "Max fatigue +10 per level" },
  [StatUpgradeId.Unauffaelligkeit]: { name: "🥷 Inconspicuousness", description: "Chance of being called on −10% per level" },
  [StatUpgradeId.CriticalHit]: { name: "⚡ Critical Hit", description: "+10% chance of double fatigue reduction" },
  [StatUpgradeId.StandingDesk]: { name: "🪑 Standing Desk", description: "-1 fatigue per round (passive)" },
  [StatUpgradeId.VPN]: { name: "🔒 VPN Access", description: "+5% chance to skip a meeting" },
  [StatUpgradeId.Ohrstoepsel]: { name: "🎵 Bluetooth Earbuds", description: "Colleague fatigue −1 per speaking turn" },
  [StatUpgradeId.Kreditkarte]: { name: "💳 Company Credit Card", description: "Salary penalties −10% per level" },
};

export const POWERUPS: Record<PowerupId, TextEntry> = {
  [PowerupId.Kaffee]: { name: "☕ Coffee", description: "Instantly −25 fatigue" },
  [PowerupId.HandyAkku]: { name: "📱 Phone Battery", description: "Next procrastination: 0% chance of being caught" },
  [PowerupId.NoiseCancelling]: { name: "🎧 Noise Cancelling", description: "Colleague fatigue −50% for one meeting" },
  [PowerupId.FakeNotizen]: { name: "📋 Fake Notes", description: "Next question is automatically correct" },
  [PowerupId.LaptopTarnung]: { name: "💻 Laptop Camouflage", description: "Procrastinating looks like work for 3 rounds" },
  [PowerupId.PizzaBestellen]: { name: "🍕 Order Pizza", description: "Skip one meeting entirely" },
  [PowerupId.MuteButton]: { name: "🔇 Mute Button", description: "Immune to being called on for one meeting" },
  [PowerupId.RemoteJoker]: { name: "📡 Remote Joker", description: "The meeting moves online — 40% less chance of being called on" },
  [PowerupId.BullshitBingo]: { name: "📊 Bullshit Bingo", description: "Count buzzwords — bonus fatigue reduction at the end of the meeting" },
  [PowerupId.PromptBibliothek]: { name: "📚 Prompt Library", description: "All minigame timers +60% for 1 meeting" },
  [PowerupId.KiZusammenfassung]: { name: "📝 AI Summary", description: "The AI already summarised it: meeting is 2 rounds shorter" },
  [PowerupId.KiAgent]: { name: "🤖 AI Agent", description: "Works through the night: +150€, but +10 fatigue from proofreading it" },
};

export const PERKS: Record<string, TextEntry> = {
  // Positive
  guterSchlaf: { name: "Slept Well", description: "20% less fatigue build-up" },
  motivationsschub: { name: "Motivation Surge", description: "+50% fatigue recovery" },
  fluesterModus: { name: "Whisper Mode", description: "20% lower chance of being called on" },
  sparsamkeit: { name: "Thrift", description: "Salary penalties -25%" },
  zuckerflash: { name: "Sugar Rush", description: "+15 max fatigue" },
  networking: { name: "Networking Skills", description: "+€50 per day survived" },
  flowState: { name: "Flow State", description: "Minigame timer +25%" },
  doppelterEspresso: { name: "Double Espresso", description: "30% less fatigue build-up" },
  chefImUrlaub: { name: "Boss on Holiday", description: "40% lower chance of being called on" },
  fruehstuecksbuffet: { name: "Breakfast Buffet", description: "+20 max fatigue and penalties -15%" },
  glueckssocken: { name: "Lucky Socks", description: "Minigame timer +40%" },
  parkplatzGefunden: { name: "Found a Parking Space!", description: "+€75 good-mood bonus" },
  kiCopilot: { name: "AI Copilot", description: "The AI thinks along: 25% less fatigue build-up" },
  promptGefunden: { name: "The Prompt Lands", description: "Finally the right prompt: minigame timer +30%" },
  agentLaeuft: { name: "The Agent Is Running", description: "It works, you recover: +40% fatigue recovery" },
  modellUpdate: { name: "Model Update", description: "The new model really is better: +20 max fatigue" },
  // Negative
  langeGezockt: { name: "Up Late Gaming", description: "+20 fatigue at the start of the day" },
  kater: { name: "Hangover", description: "+25% fatigue build-up" },
  schlechtGeschlafen: { name: "Slept Badly", description: "-30% fatigue recovery" },
  auffaellig: { name: "Conspicuously Dressed", description: "+25% chance of being called on" },
  mieterhoehung: { name: "Rent Increase", description: "-€30 salary per day" },
  sodbrennen: { name: "Heartburn", description: "-10 max fatigue" },
  ablenkung: { name: "Distraction", description: "Minigame timer -25%" },
  montagsblues: { name: "Monday Blues", description: "A new day only heals half" },
  stau: { name: "Stuck in Traffic", description: "+15 fatigue and penalties +20%" },
  tastaturDefekt: { name: "Broken Keyboard", description: "Minigame timer -35%" },
  kantineGeschlossen: { name: "Kantine Closed", description: "-50% fatigue recovery and -15 max fatigue" },
  druckerStau: { name: "Paper Jam", description: "+30% chance of being called on" },
  windowsUpdate: { name: "Windows Update", description: "A new day only heals 30%" },
  kiAusfall: { name: "AI Outage", description: "Today you have to think for yourself: -40% fatigue recovery" },
  halluziniertesProtokoll: {
    name: "Hallucinated Minutes",
    description: "The AI writes commitments into the minutes on your behalf: +30% chance of being called on",
  },
  kontextfensterVoll: {
    name: "Context Window Full",
    description: "The AI forgot the beginning, you explain it again: minigame timer -30%",
  },
  promptInjection: {
    name: "Prompt Injection",
    description: "Somebody slipped your name into the agenda: +35% chance of being called on",
  },
};

export const ACHIEVEMENTS: Record<AchievementId, TextEntry> = {
  [AchievementId.MeetingMarathon]: { name: "Meeting Marathon", description: "Survive 10 meetings in one day" },
  [AchievementId.Unsichtbar]: { name: "Invisible", description: "Get through a meeting without being called on" },
  [AchievementId.Schlafwandler]: { name: "Sleepwalker", description: "Fall asleep 3× and still survive the day" },
  [AchievementId.BullshitBingo]: { name: "Bullshit Bingo", description: "Hear 15+ buzzwords in one meeting (with a bingo card)" },
  [AchievementId.Unantastbar]: { name: "Untouchable", description: "Procrastinate 20× in a row without being caught" },
  [AchievementId.MonologKoenig]: { name: "Monologue King", description: "Deliver 5 unprompted monologues in one day" },
  [AchievementId.Speedrunner]: { name: "Speedrunner", description: "Answer every question in a meeting correctly (min. 2)" },
  [AchievementId.Gehaltsverhandler]: { name: "Salary Negotiator", description: "End a day with more salary than you started" },
  [AchievementId.Fluesterer]: { name: "Whisperer", description: "Get through 3 meetings speaking only when called on" },
  [AchievementId.Legend]: { name: "Legend", description: "Survive 30 days" },
  [AchievementId.KoffeinJunkie]: { name: "Caffeine Junkie", description: "Use 10 coffee powerups in total" },
  [AchievementId.TeflonBeschichtung]: { name: "Teflon Coated", description: "Survive 5 board meetings" },
  [AchievementId.Kartenhai]: { name: "Card Shark", description: "Win 5 card draws in prep time" },
  [AchievementId.MinigameMeister]: { name: "Minigame Master", description: "Win 20 minigames in total" },
  [AchievementId.Ueberlebenskuenstler]: { name: "Escape Artist", description: "Survive a day on under €1,200" },
};

export const ENDINGS: Record<EndingType, EndingContent> = {
  [EndingType.Fired]: {
    title: "TERMINATED",
    description: "Your salary fell below the minimum wage. HR has invited you to a conversation... one last time.",
  },
  [EndingType.Burnout]: {
    title: "BURNOUT",
    description: "You fell asleep five times in a single day. The company doctor has signed you off. Permanently.",
  },
  [EndingType.Retired]: {
    title: "EARLY RETIREMENT",
    description: "You have banked enough meeting-hours and are off into a thoroughly earned early retirement!",
  },
  [EndingType.Promoted]: {
    title: "PROMOTED",
    description: "Unbelievable! You have been promoted to Senior. Now YOU schedule the meetings. The circle closes.",
  },
  [EndingType.Whistleblower]: {
    title: "WHISTLEBLOWER",
    description: "Twenty completed bullshit-bingo cards is evidence enough. You leaked the lot to the press!",
  },
};

export const PERMANENT_UNLOCKS: Record<PermanentUnlockId, PermanentUnlockContent> = {
  [PermanentUnlockId.Firmenwagen]: {
    name: "Company Car",
    description: "The company provides you with a car.",
    effect: "+€100 starting salary",
  },
  [PermanentUnlockId.BueroHund]: {
    name: "Office Dog",
    description: "A dog in the office lowers everybody's stress level.",
    effect: "-5% fatigue build-up, permanently",
  },
  [PermanentUnlockId.KantineFlatrate]: {
    name: "Kantine Flat Rate",
    description: "All you can eat in the company canteen.",
    effect: "+20 max fatigue, permanently",
  },
  [PermanentUnlockId.GoldeneBadge]: {
    name: "Golden Employee Badge",
    description: "Your badge gleams. Colleagues respect you.",
    effect: "-8% chance of being called on, permanently",
  },
  [PermanentUnlockId.Homeoffice]: {
    name: "Home-Office Contract",
    description: "15% chance that a meeting is online by default.",
    effect: "15% chance of an online meeting",
  },
  [PermanentUnlockId.Dienstwohnung]: {
    name: "Company Flat",
    description: "A flat right next to the office. You sleep better.",
    effect: "+15% daily recovery",
  },
};

export const VENDORS: Record<string, VendorContent> = {
  baeckerauto: {
    name: "The Bakery Van",
    intro: "The bakery van is in the car park! The driver also has... other things on offer.",
    itemLabels: {
      [PowerupId.Kaffee]: "☕ Coffee (−25 fatigue)",
      [PowerupId.NoiseCancelling]: "🎧 Noise cancelling (−50% colleague fatigue)",
      [PowerupId.MuteButton]: "🔇 Mute button (immune to being called on)",
      [PowerupId.FakeNotizen]: "📋 Fake notes (auto-answer)",
    },
  },
  hardwarelieferant: {
    name: "Group Order, Hardware Supplier",
    intro: "IT has put in a group order with the hardware supplier. You can add something to it...",
    itemLabels: {
      [PowerupId.RemoteJoker]: "📡 Remote joker (meeting goes online)",
      [PowerupId.LaptopTarnung]: "💻 Laptop camouflage (3 rounds invisible)",
      [PowerupId.HandyAkku]: "📱 Phone battery (one safe procrastination)",
      [PowerupId.PizzaBestellen]: "🍕 Order pizza (skip a meeting)",
    },
  },
  kaffeedealer: {
    name: "The Coffee Dealer from Accounting",
    intro: "Torsten from accounting is dealing premium coffee behind the photocopier again.",
    itemLabels: {
      [PowerupId.Kaffee]: "☕ Coffee (−25 fatigue)",
      [PowerupId.BullshitBingo]: "📊 Bullshit bingo card",
      [PowerupId.NoiseCancelling]: "🎧 Noise cancelling",
    },
  },
};

export const BINGO_GOALS: Record<string, string> = {
  survive: "Survive the meeting",
  noSleep: "Stayed awake",
  noCatch: "Never caught",
  minigame: "Won a minigame",
  monologue: "Delivered a monologue",
  invisible: "Stayed invisible",
  question: "Answered correctly",
  proc3x: "Procrastinated 3×",
  dirChange: "Witnessed a direction change",
};

export const WEEKEND_EVENTS: Record<WeekendEventType, MeetingTypeContent> = {
  [WeekendEventType.TeamBuilding]: {
    name: "Team Building",
    description: "Team-building day! Trust exercises, a high-ropes course and compulsory fun.",
  },
  [WeekendEventType.Betriebsfeier]: {
    name: "Company Party",
    description: "Company party! A warm buffet, bad music and the boss doing karaoke.",
  },
  [WeekendEventType.Sommerfest]: {
    name: "Summer Party",
    description: "Summer party in the company car park. Bratwurst and networking.",
  },
  [WeekendEventType.Weihnachtsfeier]: {
    name: "Christmas Party",
    description: "Christmas party! Secret Santa, mulled wine and emotional speeches from the boss.",
  },
  [WeekendEventType.HackDay]: {
    name: "Hack Day",
    description: "Hack Day! Eight hours of coding... in meetings about the coding.",
  },
};

export const POSITIVE_EVENTS: Record<string, PositiveEventContent> = {
  freeCoffee: { title: "☕ Free coffee!", description: "Somebody left a fresh pot in the kitchen." },
  donuts: { title: "🍩 Donuts in the break room!", description: "The boss is paying. Sugar boost!" },
  fireAlarm: { title: "🔥 Fire alarm!", description: "Everybody out! The next meeting is cancelled." },
  overtimeBonus: { title: "💸 Overtime bonus!", description: "HR paid out a bonus by mistake." },
  meetingCancelled: { title: "🏠 Meeting cancelled!", description: "The organiser is off sick — meeting cancelled!" },
  mindfulness: { title: "🧘 Mindfulness break!", description: "HR prescribes five minutes of meditation." },
  birthdayCake: { title: "🎂 Birthday cake!", description: "Somebody has a birthday. Cake for everyone!" },
  praise: { title: "📧 Praise from the boss!", description: "A rare email: 'Good work!' And a pay bump!" },
};
